import { Checkbox, Spinner } from '@apollo/orbit';
import { clone, cloneDeep, sortBy } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { FilterProvider } from 'src/components/common/filterProvider/FilterProvider';
import { Loading } from 'src/components/common/loading/Loading';
import {
  DEFAULT_SEARCH_VALUE,
  SearchFilter,
} from 'src/components/searchFilter/SearchFilter';
import { useSelectedClients } from 'src/hooks/useSelectedClients';
import { semverSort } from 'src/lib/semver';

import { ClientFilterEmpty } from './clientFilterEmpty/ClientFilterEmpty';
import { ClientFilterItem } from './clientFilterItem/ClientFilterItem';
import { ClientFilterLayout } from './clientFilterLayout/ClientFilterLayout';
import { ClientFilterTitle } from './clientFilterTitle/ClientFilterTitle';
import { ClientFilterVersionItem } from './clientFilterVersionItem/ClientFilterVersionItem';
import { searchClientNameAndVersion } from './searchClientNameAndVersion';

type Writeable<T> = { -readonly [P in keyof T]-?: T[P] };
export type ClientVersions = '*' | string[];
export type SelectedClients = Record<string, ClientVersions>;

/**
 * Copy `selectedClients` and add entries for all clients in `clients` missing
 * from `selectedClients`.
 *
 * This will ensure that `selectedClients` has the same keys (representing
 * client names) as `clients`.
 */
function addMissingClientsToSelectedClients({
  allClients,
  selectedClients,
}: {
  allClients: ClientFilterViewProps['allClients'];
  selectedClients?: SelectedClients;
}): SelectedClients {
  return Object.keys(allClients).reduce<SelectedClients>(
    (accumulator, clientName) => {
      accumulator[clientName] = !selectedClients
        ? '*'
        : selectedClients[clientName] || [];

      return accumulator;
    },
    {},
  );
}

/**
 * Determine if **all** versions selected in a client's `clientVersions`.
 */
function areAllVersionsSelectedInClient({
  clientVersions,
  selectedVersions,
  withAnyVersion,
}: {
  clientVersions: string[];
  selectedVersions?: ClientVersions;
  withAnyVersion: boolean;
}) {
  // if we have the "any" version option, we want that to be the only way to '*'
  if (withAnyVersion) {
    return !selectedVersions || selectedVersions === '*';
  }
  return clientVersions.every(
    (clientVersion) =>
      !!selectedVersions &&
      (selectedVersions === '*' || selectedVersions.includes(clientVersion)),
  );
}

/**
 * Determine if **no** versions selected in a client's `clientVersions`.
 */
function areNoVersionsSelectedInClient({
  clientVersions,
  selectedVersions,
}: {
  clientVersions: string[];
  selectedVersions?: ClientVersions;
}) {
  return clientVersions.every(
    (clientVersion) =>
      !!selectedVersions &&
      selectedVersions !== '*' &&
      !selectedVersions.includes(clientVersion),
  );
}

/**
 * Determine if **all** clients and all of each client's versions are selected.
 */
function areAllClientsSelected({
  selectedClients,
  allClients,
  withAnyVersion,
}: {
  allClients: ClientFilterViewProps['allClients'];
  selectedClients: SelectedClients;
  withAnyVersion: boolean;
}): boolean {
  return (
    !selectedClients ||
    Object.entries(allClients).every(([clientName, clientVersions]) => {
      // selectedClilents[clientName] is undefined, it means we've "unchecked" a
      // client in the filter and deleted its key in selectedClients. we don't
      // need to calculate if all versions are selected in this case, we can
      // just immediately return false. not checking for this was previously
      // causing a bug no the client exclusion list in the checks page.
      return selectedClients[clientName]
        ? areAllVersionsSelectedInClient({
            clientVersions,
            selectedVersions: selectedClients[clientName],
            withAnyVersion,
          })
        : false;
    })
  );
}

/**
 * Given a set of selected clients, transform the result into a minimal form.
 * This mutates the original object passed in.
 */
function sanitizeSelectedClients({
  allClients,
  selectedClients,
  preserveClientNames,
  withAnyVersion,
}: {
  allClients: ClientFilterViewProps['allClients'];
  selectedClients: SelectedClients;
  preserveClientNames: boolean;
  withAnyVersion: boolean;
}): SelectedClients | null {
  Object.entries(selectedClients).forEach(([clientName, selectedVersions]) => {
    if (selectedVersions.length === 0) {
      // Delete clients that have no selected versions;
      // eslint-disable-next-line no-param-reassign
      delete selectedClients[clientName];
    } else if (
      areAllVersionsSelectedInClient({
        clientVersions: allClients[clientName] ?? [],
        selectedVersions,
        withAnyVersion,
      })
    ) {
      // Rewrite "all versions checked" as '*'
      // eslint-disable-next-line no-param-reassign
      selectedClients[clientName] = '*';
    }
  });
  // Return 'null' for "all clients / all versions"
  if (
    !preserveClientNames &&
    areAllClientsSelected({
      selectedClients,
      allClients,
      withAnyVersion,
    })
  ) {
    return null;
  }
  return selectedClients;
}

/**
 * Generate a representation for "all" clients being selected.
 * When preserveClientNames is true, this will be the list of all clients,
 * otherwise we use null to represent "all" as a wildcard / shorthand.
 */
function sanitizedAllClients(
  allClients: ClientFilterViewProps['allClients'],
  preserveClientNames: boolean,
): SelectedClients | null {
  return !preserveClientNames
    ? null
    : Object.keys(allClients).reduce<SelectedClients>((repr, clientName) => {
        // eslint-disable-next-line no-param-reassign
        repr[clientName] = '*';
        return repr;
      }, Object.create(null));
}

interface ClientFilterViewProps {
  allClients: Record<string, Array<string>>;
  defaultCollapsed?: boolean;
  /**
   * When true, client selection will not be converted into the wildcard /
   * shorthand version of `null` when all clients are selected.  This allows
   * for selecting an explicit set of clients without transforming into "any"
   * past / future clients (which would happen when using the `null` representation).
   * An idea for a better solution here could be to refactor this class to have
   * the caller pass in a "ClientFilterDataModel" interface which supports
   * modeling different data serialization behaviors.
   */
  preserveClientNames?: boolean;
  showAnyVersionOption?: boolean;
  selectedClients?: SelectedClients;
  setSelectedClients: (clients: SelectedClients | null) => void;
  onClientCollapsed?: (clientName: string, expanded: boolean) => void;
  canViewVersions?: boolean;
  clientThresholdToShowSearch?: number;
  title?: string;
  showReset?: boolean;
}

export const ClientFilterView = React.forwardRef<
  HTMLInputElement,
  ClientFilterViewProps
>(
  (
    {
      allClients,
      defaultCollapsed = true,
      preserveClientNames = false,
      showAnyVersionOption = false,
      selectedClients: unnormalizedSelectedClients,
      setSelectedClients,
      onClientCollapsed,
      canViewVersions,
      clientThresholdToShowSearch,
      title,
      showReset = false,
    },
    initialFocusRef,
  ) => {
    // Make sure the keys (meaning client names) are a 1:1 match between clients
    // and selected clients. Also, make `selectedClients` readonly so no callbacks
    // accidentally mutate it.
    const selectedClients = useMemo<
      Readonly<ReturnType<typeof addMissingClientsToSelectedClients>>
    >(
      () =>
        addMissingClientsToSelectedClients({
          allClients,
          selectedClients: unnormalizedSelectedClients,
        }),
      [allClients, unnormalizedSelectedClients],
    );
    /**
     * Callback to handle when a client's check box is clicked
     */
    const handleClientSelected = useCallback(
      ({ checked, name }: { checked: boolean; name: string }) => {
        // If there is no client information set in the URL, then default to the
        // full clients list. This happens when the user is in the "select all"
        // state as a default of having no filters set.

        const newSelectedClients =
          clone<Writeable<typeof selectedClients>>(selectedClients);

        if (!checked) {
          // Remove the current client from the list of selected clients
          delete newSelectedClients[name];
        } else {
          // Select all client versions for this client by copying the list of all
          // client versions into `newSelectedClients`.
          newSelectedClients[name] = '*';
        }

        setSelectedClients(
          sanitizeSelectedClients({
            allClients,
            selectedClients: newSelectedClients,
            preserveClientNames,
            withAnyVersion: showAnyVersionOption,
          }),
        );
      },
      [
        allClients,
        selectedClients,
        setSelectedClients,
        preserveClientNames,
        showAnyVersionOption,
      ],
    );

    const handleOnlyClientSelected = useCallback(
      ({ name }: { name: string }) => {
        const newSelectedClients: SelectedClients = { [name]: '*' };
        setSelectedClients(
          sanitizeSelectedClients({
            allClients,
            selectedClients: newSelectedClients,
            preserveClientNames,
            withAnyVersion: showAnyVersionOption,
          }),
        );
      },
      [
        allClients,
        setSelectedClients,
        preserveClientNames,
        showAnyVersionOption,
      ],
    );

    const allClientsSelected = areAllClientsSelected({
      selectedClients,
      allClients,
      withAnyVersion: showAnyVersionOption,
    });

    const noClientsSelected =
      !!selectedClients &&
      Object.entries(allClients).every(([clientName, clientVersions]) =>
        areNoVersionsSelectedInClient({
          clientVersions,
          selectedVersions: selectedClients[clientName],
        }),
      );

    const handleVersionClick = useCallback(
      ({
        checked,
        clientName,
        version,
      }: {
        checked: boolean;
        clientName: string;
        version: string;
      }) => {
        // We're going to mutate `selectedClients` so make a copy first.
        const updatedSelectedClients =
          cloneDeep<Writeable<typeof selectedClients>>(selectedClients);

        let selectedVersions = updatedSelectedClients[clientName];
        if (!Array.isArray(selectedVersions)) {
          selectedVersions = allClients[clientName]?.slice(0) ?? [];
        }

        if (checked) {
          updatedSelectedClients[clientName] =
            version === '*' ? '*' : selectedVersions.concat(version);
        } else {
          updatedSelectedClients[clientName] = selectedVersions.filter(
            (selectedVersion) => selectedVersion !== version,
          );
        }

        setSelectedClients(
          sanitizeSelectedClients({
            allClients,
            selectedClients: updatedSelectedClients,
            preserveClientNames,
            withAnyVersion: showAnyVersionOption,
          }),
        );
      },
      [
        allClients,
        selectedClients,
        setSelectedClients,
        preserveClientNames,
        showAnyVersionOption,
      ],
    );

    const clientCount = Object.keys(allClients).length;
    const shouldShowSearch =
      clientThresholdToShowSearch !== undefined &&
      clientCount >= clientThresholdToShowSearch;
    const [searchInputValue, setSearchInputValue] =
      useState(DEFAULT_SEARCH_VALUE);
    const filteredClients = useMemo(() => {
      return searchInputValue === DEFAULT_SEARCH_VALUE
        ? allClients
        : searchClientNameAndVersion({
            clients: allClients,
            searchTerm: searchInputValue,
          });
    }, [allClients, searchInputValue]);
    return (
      <ClientFilterLayout>
        <div className="sticky top-0 z-10 bg-primary px-4 pt-4">
          <div className="flex justify-between">
            {title ? <ClientFilterTitle>{title}</ClientFilterTitle> : null}
            {showReset && !allClientsSelected ? (
              <ClickableText
                className="rounded text-base text-blue focus-visible:ring-2"
                onClick={() => {
                  setSelectedClients(null);
                  setSearchInputValue(DEFAULT_SEARCH_VALUE);
                }}
              >
                Reset
              </ClickableText>
            ) : null}
          </div>
          {shouldShowSearch ? (
            <SearchFilter
              placeholderText="Search for a client or version"
              inputValue={searchInputValue}
              setInputValue={setSearchInputValue}
              ref={initialFocusRef}
            />
          ) : null}
        </div>
        <div className="px-4 pb-4">
          {searchInputValue ? null : (
            <Checkbox
              className="group flex items-center rounded px-2 py-[6px] text-base font-semibold hover:bg-btn-secondary-hover"
              isChecked={allClientsSelected}
              isIndeterminate={!noClientsSelected && !allClientsSelected}
              onChange={() => {
                const allClientsRepresentation = sanitizedAllClients(
                  allClients,
                  preserveClientNames,
                );
                // If all clients are selected, then deselect everything. If there are
                // no clients selected or a partial set of clients selected, then
                // select all of the clients.
                setSelectedClients(
                  allClientsSelected ? {} : allClientsRepresentation,
                );
              }}
              data-testid="client-filter-checkbox"
            >
              <div className="flex items-center">
                All clients
                <div className="ml-2 inline-block rounded-lg border border-primary px-2 text-sm font-semibold">
                  {clientCount}
                </div>
              </div>
            </Checkbox>
          )}
          {sortBy(Object.entries(filteredClients), ([clientName]) =>
            clientName.toLowerCase(),
          ).map(([clientName, clientVersions]) => {
            const selectedVersions =
              selectedClients && selectedClients[clientName];

            const isChecked: boolean | 'mixed' = areAllVersionsSelectedInClient(
              {
                clientVersions,
                selectedVersions,
                withAnyVersion: showAnyVersionOption,
              },
            )
              ? true
              : areNoVersionsSelectedInClient({
                    clientVersions,
                    selectedVersions,
                  })
                ? false
                : 'mixed';

            return (
              <ClientFilterItem
                canViewVersions={canViewVersions}
                isChecked={isChecked === true}
                isIndeterminate={isChecked === 'mixed'}
                defaultCollapsed={defaultCollapsed}
                key={clientName}
                name={clientName}
                onClientSelected={handleClientSelected}
                onOnlyClientSelected={handleOnlyClientSelected}
                onClientCollapsedChanged={onClientCollapsed}
              >
                {/* when we have a non-empty client name, show version checkboxes.
                insert a pseudo version to represent "all versions" past and future */}
                <>
                  {clientName !== ''
                    ? [
                        ...(showAnyVersionOption &&
                        !clientVersions.includes('*')
                          ? ['*']
                          : []),
                        ...semverSort(clientVersions),
                      ].map((version) =>
                        version === 'loading' ? (
                          <Spinner key={version} size="tiny" />
                        ) : (
                          <ClientFilterVersionItem
                            key={version}
                            name={clientName}
                            version={version}
                            onClientSelected={handleVersionClick}
                            selected={
                              selectedVersions
                                ? selectedVersions.includes('*') ||
                                  selectedVersions.includes(version)
                                : true
                            }
                          />
                        ),
                      )
                    : null}
                </>
              </ClientFilterItem>
            );
          })}
        </div>
      </ClientFilterLayout>
    );
  },
);

type ClientFilterProps = {
  serviceId: string;
} & Omit<
  React.ComponentProps<typeof ClientFilterView>,
  'allClients' | 'setSelectedClients'
>;

export const ClientFilter = React.forwardRef<
  HTMLInputElement,
  ClientFilterProps
>(
  (
    { serviceId, canViewVersions, title, ...passthroughProps },
    initialFocusRef,
  ) => {
    const [unnormalizedSelectedClients, setSelectedClients] =
      useSelectedClients();

    return (
      <div data-testid="clients-filter">
        <FilterProvider>
          {({ allClients, loadingClients }) => {
            if (loadingClients) {
              return (
                <div className="p-4">
                  <ClientFilterTitle>{title}</ClientFilterTitle>
                  <Loading size="xs" />
                </div>
              );
            }

            if (!allClients || Object.keys(allClients).length === 0)
              return <ClientFilterEmpty title={title} />;

            const clientNames = Object.keys(allClients);
            const clientVersions = Object.values(allClients).flatMap((x) => x);
            if (
              clientNames.length === 1 &&
              clientVersions.length === 1 &&
              clientNames[0] === '' &&
              clientVersions[0] === ''
            )
              return <ClientFilterEmpty title={title} />;

            return (
              <ClientFilterView
                canViewVersions={canViewVersions}
                allClients={allClients}
                selectedClients={unnormalizedSelectedClients}
                setSelectedClients={setSelectedClients}
                title={title}
                ref={initialFocusRef}
                {...passthroughProps}
              />
            );
          }}
        </FilterProvider>
      </div>
    );
  },
);
